import { storage } from '../firebase';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
  getMetadata,
  uploadBytesResumable
} from 'firebase/storage';
import { updateUserStorage } from './firestore';

/**
 * File Storage Utilities for Firebase Storage
 * Handles file uploads, downloads, and management with user isolation
 */

// File type configurations
const FILE_TYPES = {
  images: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  videos: ['video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov'],
  documents: [
    'application/pdf',
    'text/plain',
    'text/markdown',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'application/json',
    'application/zip',
    'application/x-zip-compressed'
  ],
  audio: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp3']
};

// File size limits (in bytes)
const FILE_SIZE_LIMITS = {
  free: 100 * 1024 * 1024, // 100MB
  pro: 1024 * 1024 * 1024, // 1GB
  enterprise: 10 * 1024 * 1024 * 1024 // 10GB
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB per file

/**
 * Validate file before upload
 * @param {File} file - The file to validate
 * @param {string} userPlan - User's subscription plan
 * @returns {Object} Validation result { valid: boolean, error: string }
 */
const validateFile = (file, userPlan = 'free') => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size exceeds 50MB limit. File size: ${(file.size / 1024 / 1024).toFixed(2)}MB`
    };
  }

  // Check file type
  const allTypes = [...FILE_TYPES.images, ...FILE_TYPES.videos, ...FILE_TYPES.documents, ...FILE_TYPES.audio];
  if (!allTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Unsupported file type: ${file.type}`
    };
  }

  return { valid: true };
};

/**
 * Get file category from MIME type
 * @param {string} mimeType - The MIME type
 * @returns {string} Category (images, videos, documents, audio, other)
 */
const getFileCategory = (mimeType) => {
  if (FILE_TYPES.images.includes(mimeType)) return 'images';
  if (FILE_TYPES.videos.includes(mimeType)) return 'videos';
  if (FILE_TYPES.documents.includes(mimeType)) return 'documents';
  if (FILE_TYPES.audio.includes(mimeType)) return 'audio';
  return 'other';
};

/**
 * Generate file path for user
 * @param {string} userId - User ID
 * @param {string} category - File category
 * @param {string} fileName - Original file name
 * @param {string} conversationId - Optional conversation ID
 * @returns {string} Storage path
 */
const generateFilePath = (userId, category, fileName, conversationId = null, fileGroupId = null) => {
  const timestamp = Date.now();
  const extension = fileName.split('.').pop();
  const baseName = fileName.replace(/\.[^/.]+$/, ''); // Remove extension

  let path = `users/${userId}`;

  if (conversationId) {
    path += `/conversations/${conversationId}`;
  } else {
    path += `/projects`;
  }

  if (fileGroupId) {
    // Co-locate related files under a shared uploads/{group}/
    path += `/uploads/${fileGroupId}/${baseName}.${extension}`;
  } else {
    path += `/${category}/${timestamp}_${baseName}.${extension}`;
  }

  return path;
};

/**
 * Upload file to Firebase Storage
 * @param {string} userId - User ID
 * @param {File} file - The file to upload
 * @param {Object} options - Upload options
 * @param {string} options.conversationId - Optional conversation ID
 * @param {function} options.onProgress - Progress callback
 * @param {string} options.userPlan - User plan for quota checking
 * @returns {Promise<Object>} Upload result with download URL and metadata
 */
const uploadFile = async (userId, file, options = {}) => {
  const { conversationId = null, onProgress = null, userPlan = 'free', fileGroupId = null, overrideName = null } = options;

  // Validate file
  const validation = validateFile(file, userPlan);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  try {
    const category = getFileCategory(file.type);
    const nameForPath = overrideName || file.name;
    const filePath = generateFilePath(userId, category, nameForPath, conversationId, fileGroupId);
    const storageRef = ref(storage, filePath);

    // Create upload task
    const uploadTask = uploadBytesResumable(storageRef, file);

    // Handle progress
    if (onProgress) {
      uploadTask.on('state_changed', (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress(progress);
      });
    }

    // Wait for upload completion
    const snapshot = await uploadTask;

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Get file metadata
    const metadata = await getMetadata(snapshot.ref);

    // Update user's storage usage
    await updateUserStorage(userId, file.size);

    return {
      success: true,
      filePath,
      downloadURL,
      metadata: {
        size: file.size,
        type: file.type,
        category,
        name: nameForPath,
        groupId: fileGroupId || null,
        uploadedAt: new Date().toISOString(),
        conversationId
      }
    };

  } catch (error) {
    throw new Error(`Upload failed: ${error.message}`);
  }
};

/**
 * Upload multiple files
 * @param {string} userId - User ID
 * @param {FileList|Array} files - Files to upload
 * @param {Object} options - Upload options
 * @returns {Promise<Array>} Array of upload results
 */
const uploadMultipleFiles = async (userId, files, options = {}) => {
  const results = [];
  const errors = [];

  for (let i = 0; i < files.length; i++) {
    try {
      const fileGroupId = typeof options.groupIdGenerator === 'function' ? options.groupIdGenerator(files[i], i) : null;
      const result = await uploadFile(userId, files[i], {
        ...options,
        fileGroupId,
        onProgress: options.onProgress ? (progress) => options.onProgress(i, progress) : null
      });
      results.push(result);
    } catch (error) {
      errors.push({
        file: files[i].name,
        error: error.message
      });
    }
  }

  return { results, errors };
};

/**
 * Delete file from Firebase Storage
 * @param {string} filePath - The storage path of the file
 * @param {string} userId - User ID (for storage quota update)
 * @param {number} fileSize - File size in bytes
 * @returns {Promise<boolean>} Success status
 */
const deleteFile = async (filePath, userId, fileSize) => {
  try {
    const fileRef = ref(storage, filePath);
    await deleteObject(fileRef);

    // Update user's storage usage (subtract)
    if (userId && fileSize) {
      await updateUserStorage(userId, -fileSize);
    }

    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    return false;
  }
};

/**
 * List all files for a user
 * @param {string} userId - User ID
 * @param {string} category - Optional category filter
 * @param {string} conversationId - Optional conversation filter
 * @returns {Promise<Array>} Array of file metadata
 */
const listUserFiles = async (userId, category = null, conversationId = null) => {
  try {
    let basePath = `users/${userId}`;

    if (conversationId) {
      basePath += `/conversations/${conversationId}`;
    } else if (category) {
      basePath += `/projects/${category}`;
    } else {
      basePath += '/projects';
    }

    const listRef = ref(storage, basePath);
    const result = await listAll(listRef);

    const files = [];

    // Get files from direct children
    for (const item of result.items) {
      try {
        const metadata = await getMetadata(item);
        const downloadURL = await getDownloadURL(item);

        files.push({
          path: item.fullPath,
          name: metadata.name,
          size: metadata.size,
          type: metadata.contentType,
          category: getFileCategory(metadata.contentType),
          uploadedAt: metadata.timeCreated,
          downloadURL
        });
      } catch (error) {
        console.error('Error getting file metadata:', error);
      }
    }

    // Recursively get files from subfolders
    for (const folder of result.prefixes) {
      const subFiles = await listUserFiles(userId, null, null); // This is recursive, might need optimization
      files.push(...subFiles);
    }

    return files;
  } catch (error) {
    console.error('Error listing files:', error);
    return [];
  }
};

/**
 * Get file download URL
 * @param {string} filePath - Storage path
 * @returns {Promise<string>} Download URL
 */
const getFileURL = async (filePath) => {
  try {
    const fileRef = ref(storage, filePath);
    return await getDownloadURL(fileRef);
  } catch (error) {
    throw new Error(`Failed to get download URL: ${error.message}`);
  }
};

/**
 * Get optimized image URL with Firebase Image Resizer
 * @param {string} originalURL - Original download URL
 * @param {number} width - Desired width
 * @param {number} height - Desired height
 * @param {string} format - Image format (webp, jpg, png)
 * @returns {string} Optimized image URL
 */
const getOptimizedImageURL = (originalURL, width = null, height = null, format = 'webp') => {
  if (!originalURL.includes('firebasestorage.googleapis.com')) {
    return originalURL;
  }

  let resizeParams = '';

  if (width) resizeParams += `w_${width}`;
  if (height) resizeParams += `${width ? '-' : ''}h_${height}`;

  if (resizeParams) {
    // Firebase Image Resizer URL format
    return `${originalURL}?alt=media&token=resize&${resizeParams}&f_${format}`;
  }

  return originalURL;
};

/**
 * Get file icon based on type
 * @param {string} mimeType - MIME type
 * @returns {string} FontAwesome icon name
 */
const getFileIcon = (mimeType) => {
  if (FILE_TYPES.images.includes(mimeType)) return 'fa-file-image';
  if (FILE_TYPES.videos.includes(mimeType)) return 'fa-file-video';
  if (FILE_TYPES.audio.includes(mimeType)) return 'fa-file-audio';
  if (mimeType === 'application/pdf') return 'fa-file-pdf';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'fa-file-word';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'fa-file-excel';
  if (mimeType === 'text/plain') return 'fa-file-text';
  if (mimeType === 'application/json') return 'fa-file-code';
  if (mimeType === 'application/zip') return 'fa-file-archive';

  return 'fa-file';
};

export {
  uploadFile,
  uploadMultipleFiles,
  deleteFile,
  listUserFiles,
  getFileURL,
  getOptimizedImageURL,
  getFileIcon,
  validateFile,
  getFileCategory,
  FILE_TYPES,
  FILE_SIZE_LIMITS
};
